import { Trans, useTranslation } from 'react-i18next';
import { formatNumber } from 'i18n/format';
import { t as translate } from 'i18n';
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTokens } from 'theme';
import { useAssistantSettings, useSaveAssistantSettings } from 'api/assistant';
import useAuthStore, { selectPrincipal } from 'stores/authStore';
import { formatWhen, settingsErrorText } from './copy';

function Usage({ usage }) {
  useTranslation();
  const t = useTokens();
  if (!usage) return null;
  return (
    <Box sx={{ mt: 2.5, p: 1.5, borderRadius: 1, bgcolor: t.raised }}>
      <Typography variant="caption" sx={{ display: 'block', color: t.inkMuted }}><Trans i18nKey="assistant.workspaceQuestionUsage" values={{ used: formatNumber(usage.tenant_month), limit: formatNumber(usage.tenant_month_limit) }} components={{ strong: <strong /> }} />
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: t.inkMuted }}><Trans i18nKey="assistant.userQuestionUsage" values={{ used: formatNumber(usage.user_day), limit: formatNumber(usage.user_day_limit) }} components={{ strong: <strong /> }} />
      </Typography>
    </Box>
  );
}

function Option({ title, lines }) {
  useTranslation();
  const t = useTokens();
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="body2" fontWeight={600}>{title}</Typography>
      {lines.filter(Boolean).map((line) => (
        <Typography key={line} variant="caption" sx={{ display: 'block', color: t.inkMuted }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
}

const consentLine = (provider) =>
  provider.consent
    ? translate('assistant.settingsDialogConsentGiven', { value1: formatWhen(provider.consent.at), value2: provider.consent.by ? ` · ${provider.consent.by}` : '' })
    : '';

const externalLine = (provider) =>
  provider.external ? translate('assistant.settingsDialogSomeModelsAreExternalTheProviderForwards') : '';

/**
 * The workspace's provider (who receives the data). Every member sees it;
 * an owner or admin connects it with consent. Family, model and effort are
 * each member's choices in the chat.
 */
function SettingsForm({ open, onClose }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const settings = useAssistantSettings({ enabled: open });
  const save = useSaveAssistantSettings();
  const data = settings.data;
  const providers = Array.isArray(data?.providers) ? data.providers : [];
  const canChange = Boolean(data?.can_change);
  const [provider, setProvider] = useState('');
  const [consent, setConsent] = useState(false);
  const [inheritModels, setInheritModels] = useState(true);
  const [modelIds, setModelIds] = useState([]);

  // The form is filled once per opening: a background refetch (someone
  // else's question moving the usage counters) must not undo the choice.
  const filled = useRef(false);
  const { reset } = save;
  const fetching = settings.isFetching;
  useEffect(() => {
    if (!open) {
      filled.current = false;
      return;
    }
    // Wait for this opening's own fetch: cached data may predate a save.
    if (!data || fetching || filled.current) return;
    filled.current = true;
    setProvider(data.provider || '');
    setConsent(false);
    setInheritModels(data.allowed_model_ids == null);
    setModelIds(Array.isArray(data.allowed_model_ids) ? data.allowed_model_ids : []);
    reset();
  }, [open, data, fetching, reset]);

  const chosen = providers.find((p) => p.id === provider);
  const families = chosen ? chosen.families : [];
  const models = Array.isArray(chosen?.models) ? chosen.models : [];
  const chatFamilies = families.filter((family) => models.some((model) => model.family === family.id && (inheritModels || modelIds.includes(model.id))));
  const needsConsent = Boolean(chosen && !chosen.consent);
  const busy = save.isPending;
  const storedIds = data?.allowed_model_ids;
  const sameModels = inheritModels
    ? storedIds == null
    : Array.isArray(storedIds) && storedIds.length === modelIds.length && storedIds.every((id) => modelIds.includes(id));
  const unchanged = provider === data?.provider && sameModels && !needsConsent;
  const ready = canChange && chosen && models.length > 0 && !unchanged && !(needsConsent && !consent);
  const submit = () => save.mutate({
    provider,
    consent,
    allowed_model_ids: inheritModels ? null : models.filter((m) => modelIds.includes(m.id)).map((m) => m.id),
  }, { onSuccess: onClose });

  const pickProvider = (id) => {
    setProvider(id);
    setConsent(false);
    setInheritModels(true);
    setModelIds([]);
  };
  const toggleModel = (id, checked) => setModelIds((current) => checked
    ? [...current, id] : current.filter((item) => item !== id));

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{translate('assistant.assistantPageAssistantSettings')}</DialogTitle>
      <DialogContent>
        {settings.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {settings.isError && <Alert severity="error">{translate('assistant.settingsDialogCouldNotLoadSettings')}</Alert>}
        {data && (
          <>
            {providers.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>{translate('assistant.settingsDialogNoModelsArePermittedForThisInstallation')}</Alert>
            )}
            <Typography variant="caption" sx={{ display: 'block', color: t.inkMuted, mt: 0.5 }}>
              {canChange
                ? translate('assistant.settingsDialogConnectAProviderAndEnableModelsFor')
                : translate('assistant.settingsDialogWorkspaceOwnersAndAdministratorsChooseTheProvider')}
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>{translate('assistant.settingsDialogProvider')}</Typography>
            <RadioGroup value={provider} onChange={(e) => pickProvider(e.target.value)}>
              {providers.map((p) => (
                <FormControlLabel
                  key={p.id}
                  value={p.id}
                  control={<Radio size="small" />}
                  label={<Option title={p.label} lines={[consentLine(p), externalLine(p)]} />}
                  disabled={!canChange || busy}
                />
              ))}
            </RadioGroup>

            {chosen && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1.5 }}>{translate('assistant.settingsDialogFamiliesInChat')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                  {chatFamilies.map((f) => <Chip key={f.id} size="small" variant="outlined" label={f.label} />)}
                  {chatFamilies.length === 0 && <Typography variant="body2" sx={{ color: t.inkMuted }}>{translate('assistant.settingsDialogNoModelFamiliesAvailable')}</Typography>}
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2.5 }}>{translate('assistant.settingsDialogAvailableModels')}</Typography>
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={inheritModels}
                      disabled={!canChange || busy}
                      onChange={(e) => {
                        setInheritModels(e.target.checked);
                        if (!e.target.checked) setModelIds(models.map((m) => m.id));
                      }}
                    />
                  )}
                  label={translate('assistant.settingsDialogAllAvailableProviderModels')}
                />
                <Typography variant="caption" sx={{ display: 'block', color: t.inkMuted, mb: 1 }}>
                  {inheritModels
                    ? translate('assistant.settingsDialogNewModelsFromThisProviderWillBecome')
                    : translate('assistant.settingsDialogMembersWillSeeOnlyTheSelectedModels')}
                </Typography>
                <Box role="group" aria-label={translate('assistant.settingsDialogAvailableModels')} sx={{ display: 'flex', flexDirection: 'column', p: 1.25, border: `1px solid ${t.border}`, borderRadius: 2 }}>
                  {models.map((m) => (
                    <FormControlLabel
                      key={m.id}
                      control={(
                        <Checkbox
                          size="small"
                          checked={inheritModels || modelIds.includes(m.id)}
                          disabled={!canChange || busy || inheritModels}
                          onChange={(e) => toggleModel(m.id, e.target.checked)}
                        />
                      )}
                      label={m.label}
                    />
                  ))}
                </Box>
                {!inheritModels && !models.some((m) => modelIds.includes(m.id)) && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>{translate('assistant.settingsDialogNoModelIsSelectedMembersWillNot')}</Alert>
                )}
              </>
            )}

            {canChange && needsConsent && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
                  label={
                    <Typography variant="body2">{translate(chosen.external ? 'assistant.providerConsentExternal' : 'assistant.providerConsent', { provider: chosen.label })}</Typography>
                  }
                />
              </Alert>
            )}
            <Usage usage={data.usage} />
            {save.isError && <Alert severity="error" sx={{ mt: 2 }}>{settingsErrorText(save.error)}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={busy}>
          {canChange ? translate('assistant.conversationListCancel') : translate('assistant.knowledgeHistoryClose')}
        </Button>
        {canChange && (
          <Button
            variant="contained"
            onClick={submit}
            disabled={busy || !ready}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          >{translate('assistant.settingsDialogSave')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Reset every draft and mutation when the authenticated user or tenant changes.
export default function SettingsDialog(props) {
  useTranslation();
  const principal = useAuthStore(selectPrincipal);
  return <SettingsForm key={principal || 'signed-out'} {...props} />;
}
